import { ProjectRemediationSeries, SeverityCounts } from '../../Models/Retest';

/**
 * Info is deliberately excluded everywhere the trajectory is plotted (spec §9.4): the question is
 * remediation posture, and Info findings dwarf the totals without indicating risk.
 */
export type PlottedSeverity = 'critical' | 'high' | 'medium' | 'low';

export type SeverityLabel = 'Critical' | 'High' | 'Medium' | 'Low';

export const PLOTTED_SEVERITIES: { key: PlottedSeverity; label: SeverityLabel }[] = [
  { key: 'critical', label: 'Critical' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
];

/** Total across the plotted severities. Info is never counted. */
export const totalOf = (counts: SeverityCounts): number =>
  PLOTTED_SEVERITIES.reduce((sum, s) => sum + (counts[s.key] ?? 0), 0);

/** One moment on the client-wide timeline: a delivery or a completed retest. */
export interface TimelineEvent {
  /** ISO date of the event. */
  date: string;
  kind: 'delivery' | 'retest';
  /** What happened, e.g. "Payments delivered" or "Payments retest 2". */
  label: string;
  /** The client-wide open backlog at this moment, summed across every delivered project. */
  counts: SeverityCounts;
  total: number;
}

interface ProjectState {
  time: number;
  counts: SeverityCounts;
}

interface Change {
  time: number;
  date: string;
  label: string;
  kind: 'delivery' | 'retest';
}

const EMPTY: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };

const timeOf = (iso?: string | null): number =>
  iso ? new Date(iso).getTime() : Number.NaN;

/**
 * Flattens the per-project series into one client-wide timeline.
 *
 * At every event date each project contributes its **last-known counts at or before that date** —
 * its baseline until its first retest completes, then that retest's counts, and so on. A project
 * contributes NOTHING before its own baseline date, not even zero: that is what stops a
 * later-delivered project from retroactively inflating the backlog for months it did not exist in.
 *
 * The consequence, and it is the point rather than a defect: a delivery pushes the totals UP,
 * because a newly delivered project dumps its whole finding set into the client's backlog. Only
 * retests pull them down. `kind` is exposed so a chart can mark deliveries and make the rises
 * legible instead of looking like drift.
 */
export function buildClientTimeline(series: ProjectRemediationSeries[]): TimelineEvent[] {
  const projects: { states: ProjectState[] }[] = [];
  const changes: Change[] = [];

  series.forEach(project => {
    const baselineTime = timeOf(project.baselineDate);
    // No baseline date means the project was never published, so it has no place on a timeline.
    if (Number.isNaN(baselineTime)) return;

    const states: ProjectState[] = [{ time: baselineTime, counts: project.baselineCounts }];
    changes.push({
      time: baselineTime,
      date: project.baselineDate as string,
      label: `${project.projectName} delivered`,
      kind: 'delivery',
    });

    project.points.forEach(point => {
      const pointTime = timeOf(point.date);
      if (Number.isNaN(pointTime)) return;

      states.push({ time: pointTime, counts: point.counts });
      changes.push({
        time: pointTime,
        date: point.date as string,
        label: `${project.projectName} ${(point.label || `Retest ${point.retestNumber}`).toLowerCase()}`,
        kind: 'retest',
      });
    });

    states.sort((a, b) => a.time - b.time);
    projects.push({ states });
  });

  if (changes.length === 0) return [];

  // Grouped by calendar DAY, not by exact timestamp. ReportAvailableDate is stamped with UtcNow,
  // so two projects delivered on the same day differ by hours; keying on the instant produced two
  // separate columns both labelled with that same date. The axis shows a day, so a day is the unit.
  //
  // Days are resolved in the viewer's timezone because that is what the axis labels are formatted
  // in (toLocaleDateString) -- grouping in UTC would let a column labelled "11 Aug" hold an event
  // the reader sees as the 12th.
  const byDay = new Map<string, Change[]>();
  changes.forEach(change => {
    const day = new Date(change.time);
    const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
    const bucket = byDay.get(key);
    if (bucket) bucket.push(change);
    else byDay.set(key, [change]);
  });

  return Array.from(byDay.values())
    .map(dayChanges => {
      const at = dayChanges.slice().sort((a, b) => a.time - b.time);
      // Evaluate at the LAST change of the day so a column reflects every event that happened on
      // it. Evaluating at the first would show a same-day delivery without the retest that
      // preceded it hours earlier.
      const asOf = at[at.length - 1].time;

      const counts: SeverityCounts = { ...EMPTY };

      projects.forEach(({ states }) => {
        // Nothing before this project's own baseline.
        if (asOf < states[0].time) return;

        let latest = states[0].counts;
        states.forEach(state => {
          if (state.time <= asOf) latest = state.counts;
        });

        PLOTTED_SEVERITIES.forEach(({ key }) => {
          counts[key] += latest[key] ?? 0;
        });
        counts.info += latest.info ?? 0;
      });

      return {
        asOf,
        event: {
          date: at[0].date,
          // A delivery landing on the same day as a retest still reads as a delivery: it is the
          // event that explains a rise.
          kind: at.some(c => c.kind === 'delivery') ? 'delivery' : 'retest',
          label: at.map(c => c.label).join(', '),
          counts,
          total: totalOf(counts),
        } as TimelineEvent,
      };
    })
    .sort((a, b) => a.asOf - b.asOf)
    .map(({ event }) => event);
}

/** Short axis form: "12 Mar". */
export const formatShortDate = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/** Full form for tooltips and range captions: "12 Mar 2026". */
export const formatFullDate = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};
